import { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './style.less';

export const Lightbox = ({ images, currentIndex, isOpen, onClose, onNext, onPrev }) => {
  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          onPrev();
          break;
        case 'ArrowRight':
          onNext();
          break;
        default:
          break;
      }
    },
    [isOpen, onClose, onNext, onPrev]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!images || images.length === 0) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="lightbox-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={handleBackdropClick}
          role="dialog"
          aria-modal="true"
          aria-label="Image lightbox"
        >
          <button
            className="lightbox-close"
            onClick={onClose}
            aria-label="Close lightbox"
            tabIndex={0}
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>

          <div className="lightbox-content">
            {images.length > 1 && (
              <button
                className="lightbox-nav lightbox-nav-prev"
                onClick={onPrev}
                aria-label="Previous image"
                tabIndex={0}
              >
                <i className="fas fa-chevron-left" aria-hidden="true"></i>
              </button>
            )}

            <motion.div
              className="lightbox-image-container"
              key={currentIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
            >
              <img
                src={images[currentIndex]}
                alt={`${currentIndex + 1} of ${images.length}`}
                className="lightbox-image"
              />
            </motion.div>

            {images.length > 1 && (
              <button
                className="lightbox-nav lightbox-nav-next"
                onClick={onNext}
                aria-label="Next image"
                tabIndex={0}
              >
                <i className="fas fa-chevron-right" aria-hidden="true"></i>
              </button>
            )}
          </div>

          {images.length > 1 && (
            <div className="lightbox-counter">
              {currentIndex + 1} / {images.length}
            </div>
          )}

          {images.length > 1 && (
            <div className="lightbox-thumbnails">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  className={`lightbox-thumbnail ${idx === currentIndex ? 'active' : ''}`}
                  onClick={() => onNext(idx)}
                  aria-label={`View image ${idx + 1}`}
                  tabIndex={0}
                >
                  <img src={img} alt={`Thumbnail ${idx + 1}`} />
                </button>
              ))}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
